import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ApiResponse } from '../../../services/api';
import { PROJECT_DESCRIPTION_MAX_LENGTH, PROJECT_NAME_MAX_LENGTH } from '../constants';
import type { Project, ProjectFormValues } from '../types';

interface ProjectFormDialogProps {
  open: boolean;
  /** Existing project when editing; omit to create. */
  project?: Project | null;
  onSubmit: (values: ProjectFormValues) => Promise<ApiResponse<Project>>;
  onClose: () => void;
  onSuccess?: (project: Project) => void;
}

const EMPTY_VALUES: ProjectFormValues = { name: '', description: '' };

export const ProjectFormDialog: React.FC<ProjectFormDialogProps> = ({
  open,
  project,
  onSubmit,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<ProjectFormValues>(EMPTY_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = Boolean(project);
  const canSubmit = values.name.trim().length > 0 && !isSubmitting;

  useEffect(() => {
    if (!open) return;
    setValues(
      project ? { name: project.name, description: project.description ?? '' } : EMPTY_VALUES
    );
    setError(null);
  }, [open, project]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await onSubmit(values);
      if (response.success && response.data) {
        onSuccess?.(response.data);
        onClose();
        return;
      }
      setError(response.error ?? t('projects.form.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: 'dark:bg-slate-800', sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }} className="dark:text-slate-100">
        {isEdit ? t('projects.form.editTitle') : t('projects.form.createTitle')}
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4 !pt-2">
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t('projects.form.nameLabel')}
          placeholder={t('projects.form.namePlaceholder')}
          value={values.name}
          onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
          inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
          required
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          size="small"
          label={t('projects.form.descriptionLabel')}
          placeholder={t('projects.form.descriptionPlaceholder')}
          value={values.description}
          onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
          inputProps={{ maxLength: PROJECT_DESCRIPTION_MAX_LENGTH }}
        />
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          size="small"
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isEdit ? t('projects.form.save') : t('projects.form.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
